import { Component, OnInit } from '@angular/core';
import { FormControl, FormGroup } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { SpinnerService } from 'src/app/services/spinner.service';
import { Expert } from 'src/app/models/expert.schema';
import { AuthStatusService } from 'src/app/services/auth-status.service';
import { ExpertService } from 'src/app/services/expert.service';
import { AvailabilityService } from 'src/app/services/availability.service';
import { AvailabilityGroupItem } from 'src/app/constants/availability.types';
import { FormGroupToObject } from 'src/app/pipes/form-group-to-object.pipe';


type AvailabilityUserType = 'partner' | 'expert';

@Component({
  selector: 'app-availability-list',
  templateUrl: './availability-list.component.html',
  styleUrls: ['./availability-list.component.scss']
})
export class AvailabilityListComponent implements OnInit {

  userType: AvailabilityUserType;
  title = 'Disponibilità List';
  selectedDay: number | '' = this.getTodayDayOfWeek();

  defaultPage = 1;
  defaultLimit = 100;
  defaultOrder: any = { 'AvailabilityWeekly.id': 'ASC' };

  limit: number = this.defaultLimit;
  page: number = this.defaultPage;
  count: number = 0;
  maxPage: number = 0;
  filtered: any = {};
  order: any = { ...this.defaultOrder };

  availabilityForm!: FormGroup;

  groups: any;
  loggedInUserInfo: any;
  loggedInExpert: Expert;
  availabilities: AvailabilityGroupItem[] = [];

  days = [
    { dayOfWeek: 1, label: 'Lunedì' },
    { dayOfWeek: 2, label: 'Martedì' },
    { dayOfWeek: 3, label: 'Mercoledì' },
    { dayOfWeek: 4, label: 'Giovedì' },
    { dayOfWeek: 5, label: 'Venerdì' },
    { dayOfWeek: 6, label: 'Sabato' },
    { dayOfWeek: 7, label: 'Domenica' },
  ];

  constructor(
    private router: Router,
    private route: ActivatedRoute,
    private spinnerService: SpinnerService,
    private authStatusService: AuthStatusService,
    private availabilityService: AvailabilityService,
    private expertService: ExpertService,
  ) { 
    try {
      this.groups = this.authStatusService.getRoles().map((elem: string) => elem.toLowerCase());
    }
    catch {
      this.groups = [];
    }
  }

  async ngOnInit(): Promise<void> {
    try {
      this.spinnerService.show();
      this.route.data.subscribe(data => {
        this.userType = data['userType'] || 'partner';
        this.title = data['title'] || `${this.userLabel} Disponibilità Lista`;
        this.selectedDay = this.getTodayDayOfWeek();
      });
      this.manageFilterForm();
      await this.filter();
    } catch (error) {
      console.log("ERROR", error)
    } finally {
      this.spinnerService.hide();
    }
  }

  manageFilterForm(): void {
    this.availabilityForm = new FormGroup({
      'AvailabilityWeekly.id': new FormControl(''),
      'AvailabilityWeekly.startTime': new FormControl(''),
      'AvailabilityWeekly.endTime': new FormControl(''),
      'AvailabilityWeekly.available': new FormControl(''),
      [this.ownerNameFilterKey]: new FormControl(''),
      [this.provinceFilterKey]: new FormControl(''),
    });
  }

  async resetFilters() {
    this.manageFilterForm();
    this.filtered = {};
    this.page = 1;

    await this.getAvailability(this.limit, this.page, this.order);
  }

  async filter() {
    const filterForm = new FormGroupToObject().transform(this.availabilityForm);

    this.filtered = {};

    if (filterForm['AvailabilityWeekly.id']) {
      this.filtered['AvailabilityWeekly.id'] = {
        operator: 'equal',
        values: [filterForm['AvailabilityWeekly.id']],
      };
    }

    if (filterForm[this.ownerNameFilterKey]) {
      this.filtered[this.ownerNameFilterKey] = {
        operator: 'like',
        values: [`%${filterForm[this.ownerNameFilterKey]}%`],
      };
    }

    if (filterForm[this.provinceFilterKey]) {
      this.filtered[this.provinceFilterKey] = {
        operator: 'like',
        values: [`%${filterForm[this.provinceFilterKey]}%`],
      };
    }

    if (
      filterForm['AvailabilityWeekly.available'] !== undefined &&
      filterForm['AvailabilityWeekly.available'] !== ''
    ) {
      this.filtered['AvailabilityWeekly.available'] = {
        operator: 'equal',
        values: [filterForm['AvailabilityWeekly.available']],
      };
    }

    if (filterForm['AvailabilityWeekly.startTime']) {
      this.filtered['AvailabilityWeekly.startTime'] = {
        operator: 'moreThanOrEqual',
        values: [filterForm['AvailabilityWeekly.startTime']],
      };
    }

    if (filterForm['AvailabilityWeekly.endTime']) {
      this.filtered['AvailabilityWeekly.endTime'] = {
        operator: 'lessThan',
        values: [filterForm['AvailabilityWeekly.endTime']],
      };
    }

    this.page = 1;

    await this.getAvailability(this.limit, this.page, this.order);
  }

  async getAvailability(limit?: number, page?: number, order?: {}){
    let filterForm = (new FormGroupToObject()).transform(this.availabilityForm);
    this.page = page || parseInt(this.route.snapshot.queryParamMap.get('page')) || this.defaultPage;
    this.limit = limit || parseInt(this.route.snapshot.queryParamMap.get('limit')) || this.defaultLimit;
    this.order = order || JSON.parse(this.route.snapshot.queryParamMap.get('order')) || this.defaultOrder;
    this.loggedInUserInfo = await this.authStatusService.getTokenInfo();
    if(this.groups.includes('expert') && this.loggedInUserInfo){
      this.loggedInExpert = await this.expertService.getOne(this.loggedInUserInfo['extraId']);
    }
    let response = await this.availabilityService.getAvailabilityWithOwnerType(this.userType, this.page, this.limit, this.order, this.filtered);
    this.availabilities = response.availabilities || [];
    this.count = response.count;
    this.maxPage = Math.ceil(this.count / this.limit);
  }

  get userLabel(): string {
    return this.userType === 'partner' ? 'Partner' : 'Valet';
  }

  private getTodayDayOfWeek(): number {
    const jsDay = new Date().getDay();

    return jsDay === 0 ? 7 : jsDay;
  }

  selectDay(dayOfWeek: number | ''): void {
    this.selectedDay = dayOfWeek;

    this.availabilityForm.patchValue({
      searchMode: dayOfWeek === '' ? 'date' : 'day',
      dayOfWeek,
      date: '',
    });

    this.page = 1;
    this.getAvailability();
  }

  get ownerNameSortKey(): string {
    return this.userType === 'partner'
      ? 'Partner.businessName'
      : 'ExpertUser.fullName';
  }

  get provinceSortKey(): string {
    return this.userType === 'partner'
      ? 'PartnerProvince.provinceCode'
      : 'ExpertProvince.provinceCode';
  }

  get ownerNameFilterKey(): string {
    return this.userType === 'partner'
      ? 'Partner.businessName'
      : 'ExpertUser.fullName';
  }

  get provinceFilterKey(): string {
    return this.userType === 'partner'
      ? 'PartnerProvince.provinceCode'
      : 'ExpertProvince.provinceCode';
  }

  getOwner(availability: any): any {
    return this.userType === 'partner'
      ? availability?.partner
      : availability?.expert;
  }

  getOwnerId(availability: any): number | null {
    return this.getOwner(availability)?.id || null;
  }

  getOwnerName(availability: any): string {
    const owner = this.getOwner(availability);

    if (!owner) {
      return 'N/A';
    }

    if (this.userType === 'partner') {
      return owner.businessName || 'N/A';
    }

    const surname = owner.user?.surname || '';
    const name = owner.user?.name || '';
    const fullName = `${surname} ${name}`.trim();

    return fullName || 'N/A';
  }

  getAvailabilityLink(availability: any): any[] {
    const ownerId = this.getOwnerId(availability);

    if (this.userType === 'partner') {
      return ['/partner', 'availability', ownerId];
    }

    return ['/expert', 'availability', ownerId];
  }

  getOwnerProvinces(availability: any): any[] {
    const owner = this.getOwner(availability);

    if (!owner) {
      return [];
    }

    return this.userType === 'partner'
      ? owner.partnerProvinces || []
      : owner.expertProvinces || [];
  }

  getProvinceCode(item: any): string {
    return (
      item?.province?.provinceCode ||
      item?.province?.code ||
      item?.provinceCode ||
      ''
    ).toUpperCase();
  }

  getSelectedWeekly(availability: any): any | null {
    if (!availability?.weekly?.length) {
      return null;
    }

    if (this.selectedDay === '') {
      return availability.weekly.find((item: any) => !item.isClosed) || availability.weekly[0];
    }

    return availability.weekly.find((item: any) => {
      return Number(item.dayOfWeek) === Number(this.selectedDay);
    }) || null;
  }

  getSelectedWeeklySlots(availability: any): any[] {
    if (this.selectedDay === '') {
      const slots: any[] = [];

      for (const weeklyItem of availability?.weekly || []) {
        if (!weeklyItem.isClosed && weeklyItem.slots?.length) {
          slots.push(...weeklyItem.slots);
        }
      }

      return slots;
    }

    const weekly = this.getSelectedWeekly(availability);

    if (!weekly || weekly.isClosed) {
      return [];
    }

    return weekly.slots || [];
  }

  isSelectedDayAvailable(availability: any): boolean {
    if (this.selectedDay === '') {
      return (availability?.weekly || []).some((item: any) => {
        return !item.isClosed && item.slots?.length > 0;
      });
    }

    const weekly = this.getSelectedWeekly(availability);

    return !!weekly && !weekly.isClosed && weekly.slots?.length > 0;
  }

  getSerialNumber(index: number): number {
    return ((this.page || 1) - 1) * (this.limit || 100) + index + 1;
  }

  formatTimeShow(start?: string, end?: string): string {
    if (!start || !end) {
      return '---';
    }

    return `${start.substring(0, 5)} - ${end.substring(0, 5)}`;
  }

  async navigate(event) {
    await this.getAvailability(event.limit, event.page, this.order);
  }

  public navigateOrder(attribute: string | string[]) {
    delete this.order['AvailabilityWeekly.updatedAt'];
    attribute = Array.isArray(attribute) ? attribute : [attribute];
    for (let attr of attribute) {
      switch (this.order[attr]) {
        case 'ASC':
          this.order[attr] = 'DESC';
          break;
        case 'DESC':
          this.order[attr] = undefined;
          break;
        default:
          this.order = {}
          this.order[attr] = 'ASC';
          break;
      }
    }
    this.navigatePage(this.limit, this.page, { order: this.order });
  }

  navigatePage(limit: number, page: number, args: {} = {}) {
    args = this.escapeQueryStringParams(args);
    const previousArgs = Object.keys(args).length ? {} : this.route.snapshot.queryParams;
    const newParams = { ...previousArgs };
    newParams[`limit`] = limit;
    newParams[`page`] = page;
    this.router.navigate(this.route.snapshot.url.map(u => u.path), { queryParams: { ...newParams, ...args } }).then(data => {
      this.getAvailability();
    });
  }

  private escapeQueryStringParams(args: {} = {}): {} {
    Object.keys(args).forEach(key => {
      switch (Object.prototype.toString.call(args[key])) {
        case '[object Number]':
        case '[object String]':
          break;
        case '[object Array]':
        case '[object Object]':
          args[key] = JSON.stringify(args[key]);
          break;
        default:
          delete args[key];
          break;
      }
    });
    return args;
  }

  public orderIcon(attribute: string | string[]): string {
    attribute = Array.isArray(attribute) ? attribute : [attribute];
    for (let attr of attribute) {
      switch (this.order[attr]) {
        case 'ASC':
          return 'fa-sort-up';
        case 'DESC':
          return 'fa-sort-down';
        default:
          return 'fa-sort';
      }
    }
  }

}
